import React, { FC } from 'react';
import { Input, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { AccountModalSelector, Form, LocationInput, TerminalId } from '~/components';
import { Account, Terminal } from '~/types';
import { terminals } from '~/routes';
import { phoneValidation } from '~/utils';
import PhoneInput from '~/components/PhoneInput';
import useIsLocationMandatory from '~/hooks/useIsLocationMandatory';

interface TerminalFormProps {
  name: typeof terminals;
  initialValues?: Terminal;
  edit?: boolean;
}

const beforeOkCheck = (selected: Account[]) => {
  if (!Array.isArray(selected)) return false;
  const currencies = selected.map((acc) => acc.currency);
  // Check if all currencies are unique
  return currencies.length === new Set(currencies).size;
};

const TerminalForm: FC<TerminalFormProps> = (props) => {
  const { name, initialValues, edit } = props;
  const [t] = useTranslation('terminals');
  const isLocationMandatory = useIsLocationMandatory();
  const [form] = Form.useForm();
  return (
    <>
      {initialValues?.id && <TerminalId id={initialValues?.id} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" name={name} initialValues={initialValues}>
        <Form.Item name="label" label={t('label')} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="terminalType" label={t('type')} rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'MPOS', label: t('MPOS') },
              { value: 'ECOMMERCE', label: t('ECOMMERCE') },
            ]}
            disabled={initialValues?.terminalType && edit}
          />
        </Form.Item>
        <AccountModalSelector
          selectorForm={form}
          name="accountIds"
          selectMode="multiple"
          label={t('linkedAccounts')}
          beforeOkCheck={beforeOkCheck}
          required
          warning={t('linkedAccountsWarning')}
        />
        {!edit && (
          <Form.Item
            name="locationId"
            label={t('location')}
            rules={[{ required: isLocationMandatory }]}
          >
            <LocationInput allowClear />
          </Form.Item>
        )}
        <Form.Item
          name="linkedPhone"
          label={t('linkedPhone')}
          rules={[{ validator: phoneValidation }]}
        >
          <PhoneInput />
        </Form.Item>
        <Typography.Title level={5}>{t('additionalData')}</Typography.Title>
        <Form.Item name={['additionalProperties', 'NFCTerminal']} label={t('NFCTerminal')}>
          <Input />
        </Form.Item>
      </Form>
    </>
  );
};

export default TerminalForm;
